import { Dtos, IndividualContactId, SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { accountIndividualsQueryKey, accountQueryKey } from "./account.keys";

export const useDeleteAccountIndividual = (accountId: SuretyAccountId) => {
	const { _delete } = useRequest();
	const queryClient = useQueryClient();

	const individualsQueryKey = accountIndividualsQueryKey(accountId);

	const mutation = useMutation({
		mutationFn: async ({
			individualId,
		}: {
			individualId: IndividualContactId;
			onSuccess?: () => void;
		}) => {
			await _delete(
				`/v2/surety/accounts/${accountId}/individuals/${individualId}`,
				undefined,
				"none",
			);
		},
		onMutate: async ({ individualId }) => {
			await queryClient.cancelQueries({ queryKey: individualsQueryKey });
			const previousIndividuals =
				queryClient.getQueryData<Dtos.Individual.Get.Response[]>(individualsQueryKey);

			queryClient.setQueryData<Dtos.Individual.Get.Response[]>(individualsQueryKey, (old) => {
				if (!old) return [];
				return old.filter((i) => i.id !== individualId);
			});

			return { previousIndividuals };
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId) });
			variables.onSuccess?.();
		},
		onError: (_error, _variables, context) => {
			if (context?.previousIndividuals) {
				queryClient.setQueryData(individualsQueryKey, context.previousIndividuals);
			}
		},
	});

	return {
		deleteAccountIndividual: mutation.mutate,
		deleteAccountIndividualIsLoading: mutation.isPending,
		deleteAccountIndividualError: mutation.error,
	};
};
